import { useState } from 'react'
import { useParams } from 'react-router-dom'
import { Receipt, Download, XCircle, Eye } from 'lucide-react'
import { PageShell } from '@/components/common/PageShell'
import { WorkspaceToolbar } from '@/components/common/WorkspaceToolbar'
import { TableSkeleton } from '@/components/common/TableSkeleton'
import { EmptyState } from '@/components/common/EmptyState'
import { Button } from '@/components/ui/Button'
import { useMySlipView } from '@/features/payroll/hooks/usePayrollMe'
import { downloadMySlipPdf, downloadMySlipExcel } from '@/api/services/payroll-me.service'
import { triggerBlobDownload, openBlobInNewTab } from '@/utils/download'
import { StatusBadge } from '@/features/payroll/components/StatusBadge'
import { StatCard } from '@/features/payroll/components/StatCard'
import type { SlipAmountRow } from '@/features/payroll/types/payroll.types'
import { getErrorMessage } from '@/lib/getErrorMessage'

function money(amount: number, currency: string): string {
  return `${currency} ${amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
}

function hoursFmt(value?: number): string {
  if (value == null || !Number.isFinite(value)) return '—'
  const total = Math.round(value * 60)
  return `${Math.floor(total / 60)}h ${String(total % 60).padStart(2, '0')}m`
}

function InfoRow({ label, value }: { label: string; value?: string }): React.ReactElement {
  return (
    <div className="flex justify-between gap-4 py-1 text-sm">
      <span className="text-gray-500 dark:text-gray-400">{label}</span>
      <span className="font-medium text-gray-900 dark:text-gray-100">{value || '—'}</span>
    </div>
  )
}

/** 409 PAYROLL_SLIP_MIXED_CURRENCY gets its own copy; anything else is "not found". */
function slipErrorTitle(error: unknown): string {
  const code = (error as { response?: { data?: { code?: unknown } } } | null)?.response?.data?.code
  return code === 'PAYROLL_SLIP_MIXED_CURRENCY'
    ? getErrorMessage(error)
    : 'No salary slip found for this period'
}

export function MySlipDetailPage(): React.ReactElement {
  // The route param is named batchId for old links; it holds the history
  // row's slipId, which is an org batch id or a live payment id.
  const { batchId: slipId } = useParams<{ batchId: string }>()
  const [downloading, setDownloading] = useState<'view' | 'pdf' | 'excel' | null>(null)

  const { data: vm, isLoading, isError, error } = useMySlipView(slipId ?? null)

  if (isLoading) {
    return (
      <PageShell
        breadcrumbItems={[{ label: 'My Payroll', to: '/payroll/me' }, { label: 'Slip' }]}
        icon={Receipt}
        title="Salary Slip"
        className="gap-0"
        hideHeader
      >
        <TableSkeleton rows={4} cols={3} />
      </PageShell>
    )
  }

  if (isError || !vm) {
    return (
      <PageShell
        breadcrumbItems={[{ label: 'My Payroll', to: '/payroll/me' }, { label: 'Not Found' }]}
        icon={Receipt}
        title="Salary Slip"
        className="gap-0"
        hideHeader
      >
        <EmptyState icon={XCircle} title={slipErrorTitle(error)} />
      </PageShell>
    )
  }

  const cur = vm.currency
  const ps = vm.paySummary
  const reimbursements = vm.reimbursements ?? []
  const totalReimbursements =
    ps.totalReimbursements ?? reimbursements.reduce((sum, r) => sum + r.amount, 0)

  const handleView = async (): Promise<void> => {
    if (!slipId) return
    setDownloading('view')
    try {
      openBlobInNewTab(await downloadMySlipPdf(slipId))
    } finally {
      setDownloading(null)
    }
  }

  const handleDownload = async (format: 'pdf' | 'excel'): Promise<void> => {
    if (!slipId) return
    setDownloading(format)
    try {
      const blob =
        format === 'pdf' ? await downloadMySlipPdf(slipId) : await downloadMySlipExcel(slipId)
      triggerBlobDownload(blob, `salary-slip-${vm.payPeriod}.${format === 'pdf' ? 'pdf' : 'xlsx'}`)
    } finally {
      setDownloading(null)
    }
  }

  const deductionRow = (r: SlipAmountRow, i: number): React.ReactElement => (
    <tr key={i} className="border-b border-gray-100 dark:border-gray-700">
      <td className="px-4 py-2.5 text-gray-900 dark:text-gray-100">{r.description}</td>
      <td className="px-4 py-2.5 text-right tabular-nums text-red-600 dark:text-red-400">
        {money(-Math.abs(r.amount), cur)}
      </td>
    </tr>
  )

  return (
    <PageShell
      breadcrumbItems={[{ label: 'My Payroll', to: '/payroll/me' }, { label: `Slip ${vm.payPeriod}` }]}
      icon={Receipt}
      title={`Salary Slip — ${vm.payPeriod}`}
      description={cur}
      className="gap-0"
      hideHeader
    >
      <div className="space-y-4">
        {vm.status && <WorkspaceToolbar actions={<StatusBadge status={vm.status} />} />}
        {vm.draft && (
          <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-700 dark:border-amber-900/50 dark:bg-amber-900/20 dark:text-amber-300">
            This payroll period has not been finalized yet. Amounts may change.
          </div>
        )}

        {/* Employee + payment details */}
        <div className="grid gap-4 md:grid-cols-2">
          <div className="rounded-lg border border-gray-200 p-4 dark:border-gray-700">
            <p className="mb-2 text-xs font-medium uppercase text-gray-500 dark:text-gray-400">
              Employee
            </p>
            <InfoRow label="Name" value={vm.employee.name} />
            <InfoRow label="Employee ID" value={vm.employee.employeeCode} />
            <InfoRow label="Designation" value={vm.employee.designation} />
            <InfoRow label="Department" value={vm.employee.department} />
            <InfoRow label="National ID" value={vm.employee.nationalId} />
          </div>
          <div className="rounded-lg border border-gray-200 p-4 dark:border-gray-700">
            <p className="mb-2 text-xs font-medium uppercase text-gray-500 dark:text-gray-400">
              Payment & Work Summary
            </p>
            <InfoRow label="Payment Method" value={vm.paymentMethod} />
            <InfoRow label="Payment Date" value={vm.paymentDate} />
            <InfoRow
              label="Working Days / Present"
              value={
                vm.workSummary.totalWorkingDays != null
                  ? `${vm.workSummary.totalWorkingDays} / ${vm.workSummary.totalDaysPresent ?? '—'}`
                  : undefined
              }
            />
            <InfoRow label="Working Hours" value={hoursFmt(vm.workSummary.totalWorkingHours)} />
            <InfoRow label="Billable Hours" value={hoursFmt(vm.workSummary.totalBillableHours)} />
          </div>
        </div>

        {/* Totals */}
        <div className="grid grid-cols-3 gap-4">
          <StatCard label="Gross Earnings" value={ps.grossEarnings} currency={cur} />
          <StatCard label="Total Deductions" value={ps.totalDeductions} currency={cur} />
          <StatCard
            label="Net Payable"
            value={ps.netPayable}
            currency={cur}
            className="border-green-200 dark:border-green-900/50"
          />
        </div>

        {/* Actions */}
        <div className="flex gap-2">
          <Button variant="secondary" size="sm" disabled={downloading !== null} onClick={() => handleView()}>
            <Eye className="mr-1.5 h-3.5 w-3.5" />
            {downloading === 'view' ? 'Opening...' : 'View PDF'}
          </Button>
          <Button variant="secondary" size="sm" disabled={downloading !== null} onClick={() => handleDownload('pdf')}>
            <Download className="mr-1.5 h-3.5 w-3.5" />
            {downloading === 'pdf' ? 'Downloading...' : 'Download PDF'}
          </Button>
          <Button variant="secondary" size="sm" disabled={downloading !== null} onClick={() => handleDownload('excel')}>
            <Download className="mr-1.5 h-3.5 w-3.5" />
            {downloading === 'excel' ? 'Downloading...' : 'Download Excel'}
          </Button>
        </div>

        {/* Earnings */}
        <div>
          <h3 className="mb-3 text-sm font-medium text-gray-700 dark:text-gray-300">Earnings</h3>
          <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
            <table className="min-w-full border-collapse text-sm">
              <thead>
                <tr className="border-b border-gray-200 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:border-gray-700 dark:text-gray-400">
                  <th className="px-4 py-3">Project / Company</th>
                  <th className="px-4 py-3">Role</th>
                  <th className="px-4 py-3 text-right">Hours</th>
                  <th className="px-4 py-3 text-right">Rate</th>
                  <th className="px-4 py-3 text-right">Amount</th>
                </tr>
              </thead>
              <tbody>
                {vm.projectBreakdown.map((r, i) => (
                  <tr key={i} className="border-b border-gray-100 dark:border-gray-700">
                    <td className="px-4 py-2.5 text-gray-900 dark:text-gray-100">{r.projectCode}</td>
                    <td className="px-4 py-2.5 text-gray-600 dark:text-gray-400">{r.role ?? '—'}</td>
                    <td className="px-4 py-2.5 text-right tabular-nums">
                      {hoursFmt(r.hoursWorked)}
                      {r.overtimeHours ? (
                        <span className="ml-1 text-xs text-amber-600">(+{hoursFmt(r.overtimeHours)} OT)</span>
                      ) : null}
                    </td>
                    <td className="px-4 py-2.5 text-right tabular-nums">
                      {r.hourlyRate != null ? r.hourlyRate.toLocaleString(undefined, { minimumFractionDigits: 2 }) : '—'}
                    </td>
                    <td className="px-4 py-2.5 text-right tabular-nums font-medium">
                      {money(r.grossAmount, cur)}
                    </td>
                  </tr>
                ))}
                {vm.additions.map((r, i) => (
                  <tr key={`add-${i}`} className="border-b border-gray-100 dark:border-gray-700">
                    <td className="px-4 py-2.5 text-gray-900 dark:text-gray-100" colSpan={4}>
                      {r.description}
                    </td>
                    <td className="px-4 py-2.5 text-right tabular-nums font-medium text-green-700 dark:text-green-400">
                      +{money(r.amount, cur)}
                    </td>
                  </tr>
                ))}
                <tr className="bg-gray-50 font-semibold dark:bg-gray-800/50">
                  <td className="px-4 py-2.5" colSpan={4}>Gross Earnings</td>
                  <td className="px-4 py-2.5 text-right tabular-nums">{money(ps.grossEarnings, cur)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        {/* Deductions */}
        <div>
          <h3 className="mb-3 text-sm font-medium text-gray-700 dark:text-gray-300">Deductions</h3>
          <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
            <table className="min-w-full border-collapse text-sm">
              <thead>
                <tr className="border-b border-gray-200 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:border-gray-700 dark:text-gray-400">
                  <th className="px-4 py-3">Description</th>
                  <th className="px-4 py-3 text-right">Amount</th>
                </tr>
              </thead>
              <tbody>
                {vm.deductions.length > 0 ? (
                  vm.deductions.map(deductionRow)
                ) : (
                  <tr>
                    <td className="px-4 py-3 text-center italic text-gray-400" colSpan={2}>
                      No deductions
                    </td>
                  </tr>
                )}
                <tr className="bg-gray-50 font-semibold dark:bg-gray-800/50">
                  <td className="px-4 py-2.5">Total Deductions</td>
                  <td className="px-4 py-2.5 text-right tabular-nums text-red-600 dark:text-red-400">
                    {money(-ps.totalDeductions, cur)}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        {/* Reimbursements: expense claims paid back here, outside gross and tax */}
        {reimbursements.length > 0 && (
          <div>
            <h3 className="mb-3 text-sm font-medium text-gray-700 dark:text-gray-300">
              Reimbursements (non-taxable)
            </h3>
            <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
              <table className="min-w-full border-collapse text-sm">
                <thead>
                  <tr className="border-b border-gray-200 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:border-gray-700 dark:text-gray-400">
                    <th className="px-4 py-3">Description</th>
                    <th className="px-4 py-3 text-right">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {reimbursements.map((r, i) => (
                    <tr key={`reimb-${i}`} className="border-b border-gray-100 dark:border-gray-700">
                      <td className="px-4 py-2.5 text-gray-900 dark:text-gray-100">{r.description}</td>
                      <td className="px-4 py-2.5 text-right tabular-nums text-green-700 dark:text-green-400">
                        +{money(r.amount, cur)}
                      </td>
                    </tr>
                  ))}
                  <tr className="bg-gray-50 font-semibold dark:bg-gray-800/50">
                    <td className="px-4 py-2.5">Total Reimbursements</td>
                    <td className="px-4 py-2.5 text-right tabular-nums text-green-700 dark:text-green-400">
                      +{money(totalReimbursements, cur)}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Net */}
        <div className="flex items-center justify-between rounded-lg bg-gray-900 px-6 py-4 text-white dark:bg-gray-800">
          <span className="text-sm uppercase tracking-wide opacity-80">Net Payable</span>
          <span className="text-2xl font-bold tabular-nums">{money(ps.netPayable, cur)}</span>
        </div>
      </div>
    </PageShell>
  )
}
